import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen,
  Clock,
  Calendar,
  MapPin,
  Star,
  CheckCircle,
  AlertCircle,
  Play,
  FileText,
  ClipboardList,
  MonitorPlay,
  User as UserIcon,
  Video,
  BarChart3
} from "lucide-react";
import { format } from "date-fns";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Course, Enrollment, User } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { PracticeVideoUpload } from "@/components/practice/PracticeVideoUpload";
import { CoachingVideoPlayer } from "@/components/practice/CoachingVideoPlayer";
import AdminWalkthroughAssistant from "@/components/practice/AdminWalkthroughAssistant";

// Real API calls to Supabase
const CourseAPI = {
  get: async (id: string) => {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        course_categories(name)
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return {
      ...data,
      category: data.course_categories?.name || 'other',
      delivery_method: data.is_online ? 'synchronous_online' : 'in_person',
      pd_hours: data.duration_hours || 0,
      target_audience: ['all']
    } as Course;
  },
};

const EnrollmentAPI = {
  filter: async (filters: any) => {
    const { data, error } = await supabase
      .from('enrollments')
      .select('*')
      .match(filters);
    
    if (error) throw error;
    return data.map(enrollment => ({
      ...enrollment,
      user_id: enrollment.participant_id,
      status: enrollment.status as 'enrolled' | 'completed' | 'cancelled' | 'waitlisted'
    })) as Enrollment[];
  },
  delete: async (id: string) => {
    const { error } = await supabase
      .from('enrollments')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },
  update: async (id: string, data: any) => {
    const { data: result, error } = await supabase
      .from('enrollments')
      .update(data)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return {
      ...result,
      user_id: result.participant_id,
      status: result.status as 'enrolled' | 'completed' | 'cancelled' | 'waitlisted'
    } as Enrollment;
  },
};

const UserAPI = {
  me: async () => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('No authenticated user');
    
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (profileError) throw profileError;
    return {
      id: user.id,
      email: user.email,
      ...profile
    } as User;
  },
  updateMyUserData: async (data: any) => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('No authenticated user');
    
    const { data: result, error } = await supabase
      .from('profiles')
      .update(data)
      .eq('id', user.id)
      .select()
      .single();
    
    if (error) throw error;
    return {
      id: user.id,
      email: user.email,
      ...result
    } as User;
  },
};

const SurveyAPI = {
  list: async () => {
    const { data, error } = await supabase
      .from('surveys')
      .select('*');
    
    if (error) throw error;
    return data || [];
  },
};

const SurveyResponseAPI = {
  filter: async (filters: any) => {
    const { data, error } = await supabase
      .from('survey_responses')
      .select('*')
      .match(filters);
    
    if (error) throw error;
    return data || [];
  },
};

const loadVideoAssignments = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('video_assignments')
      .select('*, video:videos(*)')
      .eq('teacher_id', userId)
      .order('assigned_at', { ascending: false })
      .limit(3); // Show only recent 3 assignments

    if (error) throw error;

    // Get assigner names
    const assignmentsWithNames = await Promise.all(
      (data || []).map(async (assignment) => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', assignment.assigned_by)
          .single();
        
        return {
          ...assignment,
          video: {
            ...assignment.video,
            upload_type: assignment.video.upload_type as 'admin' | 'teacher'
          },
          assigner_name: profile 
            ? `${profile.first_name} ${profile.last_name}`
            : 'Unknown'
        };
      })
    );

    return assignmentsWithNames;
  } catch (error) {
    console.error('Error loading video assignments:', error);
    return [];
  }
};

interface VideoAssignment {
  id: string;
  video_id: string;
  teacher_id: string;
  assigned_by: string;
  assigned_at: string;
  notes?: string;
  video: {
    id: string;
    title: string;
    description: string;
    video_url: string;
    thumbnail_url?: string;
    duration_seconds?: number;
    category: string;
    tags: string[];
    upload_type: 'admin' | 'teacher';
    uploaded_by: string;
    is_public: boolean;
    status: string;
    created_at: string;
  };
  assigner_name?: string;
}

interface CoursesWithDetails {
  enrolled: Course[];
  completed: Course[];
  waitlisted: Course[];
}

export default function MyLearning() {
  const [user, setUser] = useState<User | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [coursesWithDetails, setCoursesWithDetails] = useState<CoursesWithDetails>({
    enrolled: [],
    completed: [],
    waitlisted: []
  });
  const [videoAssignments, setVideoAssignments] = useState<VideoAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseSurveys, setCourseSurveys] = useState<Record<string, any>>({});
  const [selectedCourseForDetails, setSelectedCourseForDetails] = useState<Course | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [unenrollmentTarget, setUnenrollmentTarget] = useState<{ enrollment: Enrollment; course: Course } | null>(null);
  const [practiceVideos, setPracticeVideos] = useState<any[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('user');

  const checkUserRole = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single();

    if (profile?.is_admin) {
      setUserRole('admin');
    }
  };

  const loadPracticeVideos = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('practice_videos')
        .select(`
          id,
          title,
          description,
          status,
          created_at,
          video_url,
          courses!left (title),
          coaching_feedback!left (id)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const videosWithFeedbackCount = data?.map(video => ({
        ...video,
        feedback_count: video.coaching_feedback?.length || 0
      })) || [];

      setPracticeVideos(videosWithFeedbackCount);
    } catch (error) {
      console.error('Error loading practice videos:', error);
    }
  };

  const loadLearningData = useCallback(async () => {
    try {
      const currentUser = await UserAPI.me();
      
      if (currentUser.pd_hours_completed === undefined || currentUser.pd_hours_completed === null) {
        try {
          await UserAPI.updateMyUserData({
            pd_hours_completed: 0
          });
          const updatedUser = await UserAPI.me();
          setUser(updatedUser);
        } catch (error) {
          console.error('Error initializing PD hours in database:', error);
          setUser({...currentUser, pd_hours_completed: 0});
          toast.error("Failed to initialize PD hours in the database.");
        }
      } else {
        setUser(currentUser);
      }

      const [userEnrollments, allSurveys, surveyResponses, assignments] = await Promise.all([
        EnrollmentAPI.filter({ participant_id: currentUser.id }),
        SurveyAPI.list(),
        SurveyResponseAPI.filter({ user_id: currentUser.id }),
        loadVideoAssignments(currentUser.id)
      ]);
      
      setEnrollments(userEnrollments);
      setVideoAssignments(assignments);

      const surveyMap: Record<string, any> = {};
      const userSurveyResponseMap: Record<string, boolean> = {};
      allSurveys.forEach(s => {
        if (s.course_id) surveyMap[s.course_id] = s;
      });
      surveyResponses.forEach(sr => userSurveyResponseMap[sr.survey_id] = true);

      const surveyData: Record<string, any> = {};
      for (const courseId in surveyMap) {
        surveyData[courseId] = {
          survey: surveyMap[courseId],
          completed: userSurveyResponseMap[surveyMap[courseId].id] || false
        };
      }
      setCourseSurveys(surveyData);

      const enrolled: Course[] = [];
      const completed: Course[] = [];
      const waitlisted: Course[] = [];

      for (const enrollment of userEnrollments) {
        try {
          const course = await CourseAPI.get(enrollment.course_id);
          const courseWithEnrollment = { ...course, enrollment };

          switch (enrollment.status) {
            case 'enrolled':
              enrolled.push(courseWithEnrollment);
              break;
            case 'completed':
              completed.push(courseWithEnrollment);
              break;
            case 'waitlisted':
              waitlisted.push(courseWithEnrollment);
              break;
          }
        } catch (error) {
          console.error('Error loading course details:', error);
        }
      }

      setCoursesWithDetails({ enrolled, completed, waitlisted });
      
      const refreshedUser = await UserAPI.me();
      setUser(refreshedUser);

    } catch (error) {
      console.error('Error loading learning data:', error);
      toast.error("Failed to load learning data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleVideoUploadComplete = (videoId: string) => {
    setSelectedVideo(videoId);
    loadPracticeVideos();
  };

  const handleVideoSelect = (videoId: string) => {
    setSelectedVideo(videoId);
  };

  useEffect(() => {
    checkUserRole();
    loadLearningData();
    loadPracticeVideos();
  }, [loadLearningData]);

  const handleViewDetails = (course: Course) => {
    setSelectedCourseForDetails(course);
    setIsDetailsOpen(true);
  };

  const getCurrentModulePosition = (course: Course) => {
    const completedSessions = course.enrollment?.attendance_dates?.length || 0;
    const totalSessions = course.schedule?.length || 1;
    return Math.min(completedSessions, totalSessions > 0 ? totalSessions - 1 : 0);
  };

  const handleUnenroll = async () => {
    if (!unenrollmentTarget) return;

    const { enrollment, course } = unenrollmentTarget;
    try {
      await EnrollmentAPI.delete(enrollment.id);
      toast.success(`You have been unenrolled from "${course.title}".`);

      if (course.max_participants && enrollment.status === 'enrolled') {
        const waitlisted = await EnrollmentAPI.filter(
          { course_id: course.id, status: 'waitlisted' }
        );

        if (waitlisted.length > 0) {
          await EnrollmentAPI.update(waitlisted[0].id, { status: 'enrolled' });
        }
      }

      loadLearningData();
    } catch (error) {
      console.error("Failed to unenroll:", error);
      toast.error("Could not unenroll from the course. Please try again.");
    } finally {
      setUnenrollmentTarget(null);
    }
  };

  const handleMarkAsComplete = async (enrollment: Enrollment, course: Course) => {
    try {
      await EnrollmentAPI.update(enrollment.id, { 
        status: 'completed',
        completion_date: new Date().toISOString().split('T')[0]
      });
      
      // Recalculate PD hours
      await recalculatePDHours();
      
      toast.success(`"${course.title}" marked as completed!`);
    } catch (error) {
      console.error('Failed to mark course as complete:', error);
      toast.error('Failed to mark course as complete. Please try again.');
    }
  };

  const recalculatePDHours = async () => {
    if (!user) {
      toast.error("User data not loaded yet.");
      return;
    }

    try {
      const userEnrollments = await EnrollmentAPI.filter({ participant_id: user.id });
      const completedEnrollments = userEnrollments.filter(e => e.status === 'completed');
      
      let totalPdHours = 0;
      for (const enrollment of completedEnrollments) {
        try {
          const course = await CourseAPI.get(enrollment.course_id);
          totalPdHours += (course.pd_hours || 0);
        } catch (error) {
          console.error('Error loading course for PD calculation:', error);
          toast.error("Could not load details for one or more courses.");
        }
      }
      
      await UserAPI.updateMyUserData({
        pd_hours_completed: totalPdHours
      });
      
      await loadLearningData();
      
      toast.success(`PD hours recalculated: ${totalPdHours} hours.`);
    } catch (error) {
      console.error('Error recalculating PD hours:', error);
      toast.error("Failed to recalculate PD hours.");
    }
  };

  const CourseCard = ({ course, showProgress = false }: { course: Course; showProgress?: boolean }) => {
    const getStatusBadge = () => {
      switch (course.enrollment?.status) {
        case 'enrolled':
          return <Badge className="bg-primary/10 text-primary">Enrolled</Badge>;
        case 'completed':
          return <Badge className="bg-success/10 text-success">Completed</Badge>;
        case 'waitlisted':
          return <Badge className="bg-warning/10 text-warning">Waitlisted</Badge>;
        default:
          return null;
      }
    };

    const getNextSession = () => {
      if (!course.schedule?.length) return null;
      const now = new Date();
      return course.schedule.find(session => new Date(session.date) > now);
    };

    const nextSession = getNextSession();
    const surveyInfo = courseSurveys[course.id];
    const currentPosition = getCurrentModulePosition(course);

    return (
      <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-card border-primary/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg leading-tight text-foreground">{course.title}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {course.description}
              </p>
            </div>
            <div className="ml-4 space-y-2">
              {getStatusBadge()}
              {course.required && <Badge variant="outline">Required</Badge>}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4 text-primary" />
              {course.pd_hours} PD Hours
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary" />
              {course.delivery_method?.replace('_', ' ')}
              {course.location && ` • ${course.location}`}
            </div>

            {nextSession && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4 text-primary" />
                Next: {format(new Date(nextSession.date), 'MMM d, yyyy')}
                {nextSession.start_time && ` at ${nextSession.start_time}`}
              </div>
            )}

            {course.instructor && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <BookOpen className="w-4 h-4 text-primary" />
                {course.instructor}
              </div>
            )}
          </div>

          {showProgress && course.enrollment?.status === 'enrolled' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="text-foreground font-medium">
                  {course.enrollment.attendance_dates?.length || 0} / {course.schedule?.length || 0} sessions
                </span>
              </div>
              <Progress
                value={((course.enrollment.attendance_dates?.length || 0) / (course.schedule?.length || 1)) * 100}
                className="h-2"
              />
            </div>
          )}

          {course.enrollment?.status === 'completed' && (
            <div className="bg-success/10 p-3 rounded-lg space-y-2 border border-success/20">
              <div className="flex items-center gap-2 text-success">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Course Completed</span>
              </div>
              {course.enrollment.completion_date && (
                <p className="text-sm text-success">
                  Completed on {format(new Date(course.enrollment.completion_date), 'MMM d, yyyy')}
                </p>
              )}
              {course.enrollment.evaluation_score && (
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-warning fill-current" />
                  <span className="text-sm text-success">
                    Rating: {course.enrollment.evaluation_score}/5
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2 pt-2">
            {course.enrollment?.status === 'enrolled' && (
              <>
                {/* Primary Access Course Button */}
                {course.delivery_method === 'asynchronous' ? (
                  <Button asChild className="w-full bg-gradient-primary hover:bg-primary/90 transition-all duration-300 text-lg py-3">
                    <Link to={`/course-player/${course.id}`}>
                      <Play className="w-5 h-5 mr-2" />
                      Access Course
                    </Link>
                  </Button>
                ) : course.online_course_url ? (
                  <Button asChild className="w-full bg-gradient-primary hover:bg-primary/90 transition-all duration-300 text-lg py-3">
                    <a
                      href={course.online_course_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      Access Course
                    </a>
                  </Button>
                ) : (
                  <Button asChild className="w-full bg-gradient-primary hover:bg-primary/90 transition-all duration-300 text-lg py-3">
                    <Link to={`/course-player/${course.id}`}>
                      <Play className="w-5 h-5 mr-2" />
                      Access Course
                    </Link>
                  </Button>
                )}
                
                {/* Secondary Action Buttons */}
                {course.delivery_method === 'asynchronous' && (
                  <div className="flex gap-2">
                    <Button asChild variant="outline" className="flex-1">
                      <Link to={`/course-player/${course.id}`}>
                        <BookOpen className="w-4 h-4 mr-2" />
                        Start Over
                      </Link>
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => handleViewDetails(course)}>
                      <FileText className="w-4 h-4 mr-2" />
                      Details
                    </Button>
                  </div>
                )}
                
                {course.delivery_method !== 'asynchronous' && (
                  <Button variant="outline" className="w-full" onClick={() => handleViewDetails(course)}>
                    <FileText className="w-4 h-4 mr-2" />
                    View Course Details
                  </Button>
                )}
                
                <Button 
                  onClick={() => handleMarkAsComplete(course.enrollment!, course)}
                  variant="secondary" 
                  className="w-full"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Complete
                </Button>
                <Button variant="destructive" className="w-full" onClick={() => setUnenrollmentTarget({ enrollment: course.enrollment!, course })}>
                  Unenroll
                </Button>
              </>
            )}

            {course.enrollment?.status === 'completed' && (
              <>
                <Button variant="outline" className="w-full" onClick={() => handleViewDetails(course)}>
                  <FileText className="w-4 h-4 mr-2" />
                  View Details
                </Button>

                {surveyInfo && !surveyInfo.completed && (
                  <Button asChild className="w-full bg-gradient-primary hover:bg-primary/90">
                    <Link to={createPageUrl(`TakeSurvey?surveyId=${surveyInfo.survey.id}`)}>
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Complete Survey
                    </Link>
                  </Button>
                )}
                {surveyInfo && surveyInfo.completed && (
                  <Button variant="outline" disabled className="w-full">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Survey Completed
                  </Button>
                )}
              </>
            )}

            {course.enrollment?.status === 'waitlisted' && (
              <>
                <Button variant="outline" className="w-full" onClick={() => handleViewDetails(course)}>
                  <FileText className="w-4 h-4 mr-2" />
                  View Details
                </Button>
                <Button variant="destructive" className="w-full" onClick={() => setUnenrollmentTarget({ enrollment: course.enrollment!, course })}>
                  Leave Waitlist
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-64 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = user ?
    Math.min(100, ((user.pd_hours_completed || 0) / (user.required_pd_hours || 40)) * 100) : 0;

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">My Professional Learning Journey</h1>
          <p className="text-muted-foreground">Track your professional learning progress</p>
        </div>

        <Card className="bg-gradient-primary border-primary/20 shadow-glow">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-primary-foreground">Annual PD Progress</h3>
                <p className="text-primary-foreground/80">
                  {user?.pd_hours_completed || 0} of {user?.required_pd_hours || 40} hours completed
                </p>
              </div>
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="text-2xl font-bold text-primary-foreground flex-shrink-0">
                  {Math.round(progressPercentage)}%
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button onClick={recalculatePDHours} variant="secondary" size="sm">
                    Recalculate PD Hours
                  </Button>
                  <Link to={createPageUrl('Transcript')} className="flex-grow sm:flex-grow-0">
                    <Button variant="secondary" className="w-full sm:w-auto">
                      <FileText className="w-4 h-4 mr-2" />
                      View Transcript
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
            <Progress value={progressPercentage} className="h-3" />
          </CardContent>
        </Card>

        {/* Video Assignments Section */}
        {videoAssignments.length > 0 && (
          <Card className="bg-gradient-card border-primary/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MonitorPlay className="h-5 w-5 text-primary" />
                  Recent Video Assignments
                </CardTitle>
                <CardDescription>
                  Videos assigned to you by administrators
                </CardDescription>
              </div>
              <Link to="/video-assignments">
                <Button variant="outline" size="sm">
                  View All ({videoAssignments.length})
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {videoAssignments.slice(0, 3).map((assignment) => (
                  <Card key={assignment.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-sm line-clamp-2">{assignment.video.title}</CardTitle>
                        <Badge variant="secondary" className="text-xs ml-2">
                          {assignment.video.category.replace('_', ' ')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-2">
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {assignment.video.description}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <UserIcon className="h-3 w-3" />
                        By: {assignment.assigner_name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(assignment.assigned_at), 'MMM d')}
                      </div>
                      <Button asChild size="sm" className="w-full">
                        <a href={assignment.video.video_url} target="_blank" rel="noopener noreferrer">
                          <Play className="h-3 w-3 mr-1" />
                          Watch
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs defaultValue="enrolled" className="w-full">
        <TabsList className={`grid w-full bg-muted ${userRole === 'admin' ? 'grid-cols-6 max-w-4xl' : 'grid-cols-5 max-w-3xl'}`}>
          <TabsTrigger value="enrolled" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            In Progress ({coursesWithDetails.enrolled.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Completed ({coursesWithDetails.completed.length})
          </TabsTrigger>
          <TabsTrigger value="waitlisted" className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Waitlisted ({coursesWithDetails.waitlisted.length})
          </TabsTrigger>
          <TabsTrigger value="practice" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            AI Coaching ({practiceVideos.length})
          </TabsTrigger>
          <TabsTrigger value="insights" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Insights
          </TabsTrigger>
          {userRole === 'admin' && (
            <TabsTrigger value="walkthrough" className="flex items-center gap-2">
              <UserIcon className="w-4 h-4" />
              Walkthroughs
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="enrolled" className="space-y-6">
          {coursesWithDetails.enrolled.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coursesWithDetails.enrolled.map((course) => (
                <CourseCard key={course.id} course={course} showProgress={true} />
              ))}
            </div>
          ) : (
            <Card className="bg-gradient-card">
              <CardContent className="text-center py-12">
                <BookOpen className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Active Courses</h3>
                <p className="text-muted-foreground mb-4">
                  You're not currently enrolled in any courses
                </p>
                <Button onClick={() => window.location.href = createPageUrl('CourseCatalog')} className="bg-gradient-primary">
                  Browse Course Catalog
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed" className="space-y-6">
          {coursesWithDetails.completed.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coursesWithDetails.completed.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ) : (
            <Card className="bg-gradient-card">
              <CardContent className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Completed Courses</h3>
                <p className="text-muted-foreground">
                  Complete your first course to see it here
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="waitlisted" className="space-y-6">
          {coursesWithDetails.waitlisted.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {coursesWithDetails.waitlisted.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          ) : (
            <Card className="bg-gradient-card">
              <CardContent className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Waitlisted Courses</h3>
                <p className="text-muted-foreground">
                  You're not on any course waitlists
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="practice" className="space-y-6">
          {selectedVideo ? (
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Button
                  variant="outline"
                  onClick={() => setSelectedVideo(null)}
                >
                  ← Back to Videos
                </Button>
                <h2 className="text-2xl font-bold">AI Coaching Analysis</h2>
              </div>
              
              <CoachingVideoPlayer 
                videoId={selectedVideo} 
                autoAnalyze={true}
              />
            </div>
          ) : (
            <>
              <PracticeVideoUpload onUploadComplete={handleVideoUploadComplete} />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Video className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Record Your Teaching</h3>
                        <p className="text-sm text-muted-foreground">
                          Capture yourself implementing new strategies
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <BookOpen className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">AI Analysis</h3>
                        <p className="text-sm text-muted-foreground">
                          Get feedback based on educational research
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Improve Practice</h3>
                        <p className="text-sm text-muted-foreground">
                          Apply insights to enhance your teaching
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {practiceVideos.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Your Practice Videos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {practiceVideos.map(video => (
                      <Card key={video.id} className="hover:shadow-md transition-shadow cursor-pointer">
                        <CardContent className="p-0">
                          <div className="aspect-video bg-muted rounded-t-lg relative overflow-hidden">
                            <video
                              src={video.video_url}
                              className="w-full h-full object-cover"
                              preload="metadata"
                            />
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                onClick={() => handleVideoSelect(video.id)}
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Watch & Analyze
                              </Button>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold line-clamp-2">{video.title}</h3>
                              <Badge className={
                                video.status === 'analyzed' ? 'bg-green-100 text-green-800' :
                                video.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {video.status}
                              </Badge>
                            </div>
                            
                            {video.courses && (
                              <p className="text-xs text-muted-foreground mb-2">
                                Related: {video.courses.title}
                              </p>
                            )}
                            
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(video.created_at).toLocaleDateString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <BookOpen className="h-3 w-3" />
                                {video.feedback_count || 0} insights
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Practice Videos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{practiceVideos.length}</div>
                <p className="text-xs text-muted-foreground">
                  {practiceVideos.filter(v => v.status === 'analyzed').length} analyzed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">AI Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {practiceVideos.reduce((acc, v) => acc + (v.feedback_count || 0), 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  From coaching analysis
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Courses Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{coursesWithDetails.completed.length}</div>
                <p className="text-xs text-muted-foreground">
                  Professional development
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">PD Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{user?.pd_hours_completed || 0}</div>
                <p className="text-xs text-muted-foreground">
                  Hours completed
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Your Learning Journey</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Track your professional growth through AI coaching feedback and course completions.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Practice video submissions for personalized coaching
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  AI-powered feedback based on educational research
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  Professional development course completion tracking
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  Continuous improvement recommendations
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {userRole === 'admin' && (
          <TabsContent value="walkthrough" className="space-y-6">
            <AdminWalkthroughAssistant />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}